import { TIERS } from "@mw/shared/pricing";

// ---------------------------------------------------------------------------
// Per-product PAYG pricing -- single source of truth from the PAYG tier
// ---------------------------------------------------------------------------

const paygTier = TIERS.payg;

/** Approximate characters per minute of generated speech (industry standard) */
const CHARS_PER_MINUTE_OF_SPEECH = 1000;

// ---------------------------------------------------------------------------
// Per-product options for the PAYG cost calculator
// ---------------------------------------------------------------------------

export type PaygProductId =
  | "tts_flash"
  | "tts_multilingual"
  | "stt_scribe"
  | "stt_realtime"
  | "music"
  | "voice_isolator"
  | "voice_changer"
  | "sound_effects"
  | "dubbing";

export type PaygDisplayUnit = "minute" | "hour";

export interface PaygProductOption {
  id: PaygProductId;
  /** Cost per minute, used to compute the estimated monthly cost from the slider. */
  pricePerMinute: number;
  /** Headline unit price shown next to the product, in `displayUnit` (USD). */
  displayUnitPrice: number;
  displayUnit: PaygDisplayUnit;
}

export const PAYG_PRODUCT_OPTIONS: PaygProductOption[] = (() => {
  const api = paygTier.platformConfig.api;
  const ttsFlashPerMinute = (api.textToSpeech.turboUsdPer1000Chars * CHARS_PER_MINUTE_OF_SPEECH) / 1000;
  const ttsMultilingualPerMinute = (api.textToSpeech.multilingualUsdPer1000Chars * CHARS_PER_MINUTE_OF_SPEECH) / 1000;
  return [
    { id: "tts_flash", pricePerMinute: ttsFlashPerMinute, displayUnitPrice: ttsFlashPerMinute, displayUnit: "minute" },
    {
      id: "tts_multilingual",
      pricePerMinute: ttsMultilingualPerMinute,
      displayUnitPrice: ttsMultilingualPerMinute,
      displayUnit: "minute",
    },
    {
      id: "stt_scribe",
      pricePerMinute: api.speechToText.scribeUsdPerHour / 60,
      displayUnitPrice: api.speechToText.scribeUsdPerHour,
      displayUnit: "hour",
    },
    {
      id: "stt_realtime",
      pricePerMinute: api.speechToText.scribeRealtimeUsdPerHour / 60,
      displayUnitPrice: api.speechToText.scribeRealtimeUsdPerHour,
      displayUnit: "hour",
    },
    {
      id: "music",
      pricePerMinute: api.music.usdPerMinute,
      displayUnitPrice: api.music.usdPerMinute,
      displayUnit: "minute",
    },
    {
      id: "voice_isolator",
      pricePerMinute: api.voiceIsolator.usdPerMinute,
      displayUnitPrice: api.voiceIsolator.usdPerMinute,
      displayUnit: "minute",
    },
    {
      id: "voice_changer",
      pricePerMinute: api.voiceChanger.usdPerMinute,
      displayUnitPrice: api.voiceChanger.usdPerMinute,
      displayUnit: "minute",
    },
    {
      id: "sound_effects",
      pricePerMinute: api.soundEffects.usdPerMinute,
      displayUnitPrice: api.soundEffects.usdPerMinute,
      displayUnit: "minute",
    },
    {
      id: "dubbing",
      pricePerMinute: api.dubbing.autoUsdPerMinute,
      displayUnitPrice: api.dubbing.autoUsdPerMinute,
      displayUnit: "minute",
    },
  ];
})();
